import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { DjPageShell } from '@/shared/ui/DjPageShell';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { spotifyApi } from '@/entities/spotify/api/spotify.api';
import { useNotificationStore } from '@/shared/api/notificationStore';
import type { SpotifyPlaylist, SpotifyStatus } from '@/entities/spotify/model/spotify.types';
import { useAuth } from '@/shared/auth/useAuth';

const SPOTIFY_GREEN = '#1DB954';

const iconPaths = {
  spotify:
    'M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.623.623 0 0 1-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 1 1-.277-1.215c3.809-.87 7.077-.496 9.712 1.115a.623.623 0 0 1 .207.857zm1.223-2.722a.78.78 0 0 1-1.072.257c-2.687-1.652-6.785-2.131-9.965-1.166a.78.78 0 0 1-.973-.519.78.78 0 0 1 .518-.973c3.632-1.102 8.147-.568 11.234 1.329a.78.78 0 0 1 .258 1.072zm.105-2.835C14.692 8.95 9.375 8.775 6.297 9.71a.937.937 0 1 1-.543-1.795c3.532-1.07 9.404-.862 13.115 1.338a.936.936 0 0 1 .045 1.614z',
  music:
    'M12 3v11.55A4 4 0 1 0 14 18V7h6V3h-8zM8 20a2 2 0 1 1 0-4 2 2 0 0 1 0 4z',
  headphones:
    'M12 3a8 8 0 0 0-8 8v6a3 3 0 0 0 3 3h2v-8H6v-1a6 6 0 0 1 12 0v1h-3v8h2a3 3 0 0 0 3-3v-6a8 8 0 0 0-8-8z',
  wave:
    'M4 10h2v8H4v-8zm4-4h2v12H8V6zm4-3h2v15h-2V3zm4 6h2v9h-2V9zm4-4h2v13h-2V5z',
  check:
    'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z',
  link:
    'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z',
  unlink:
    'M17 7h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5zm-6 8H7c-1.71 0-3.1-1.39-3.1-3.1S5.29 8.9 7 8.9h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9zm3-4H8v-2h6v2z',
  person:
    'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
};

const featureBullets = [
  {
    icon: iconPaths.link,
    title: 'Link playlists to events',
    copy: 'Attach a Spotify playlist to any event — guests see your curation, you keep the control.',
  },
  {
    icon: iconPaths.wave,
    title: 'Request-aware queue',
    copy: 'Song requests from the crowd slot into your playlist order without you lifting a finger.',
  },
  {
    icon: iconPaths.headphones,
    title: 'Stay in the mix',
    copy: 'Approve, skip, or park requests straight from the dashboard while the set rolls.',
  },
];

const playlistGradients = [
  `linear-gradient(135deg, #5B21B6, #DB2777)`,
  `linear-gradient(135deg, #DB2777, #D97706)`,
  `linear-gradient(135deg, #059669, #5B21B6)`,
  `linear-gradient(135deg, #1DB954, #059669)`,
  `linear-gradient(135deg, #D97706, #DC2626)`,
  `linear-gradient(135deg, #7C3AED, #1DB954)`,
];

function AppIcon({ path, ...props }: SvgIconProps & { path: string }) {
  return (
    <SvgIcon {...props}>
      <path d={path} />
    </SvgIcon>
  );
}

function PlaylistCard({ playlist, index }: { playlist: SpotifyPlaylist; index: number }) {
  const theme = useTheme();
  const gradient = playlistGradients[index % playlistGradients.length];

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
        bgcolor: alpha(theme.palette.common.white, 0.065),
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        boxShadow: `0 18px 44px ${alpha(theme.palette.common.black, 0.16)}`,
        transition: 'transform 0.18s, background-color 0.18s, border-color 0.18s, box-shadow 0.18s',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${SPOTIFY_GREEN}, ${theme.palette.secondary.light}, ${theme.palette.warning.light})`,
          opacity: 0.7,
          zIndex: 1,
        },
        '&:hover': {
          bgcolor: alpha(theme.palette.common.white, 0.095),
          borderColor: alpha(SPOTIFY_GREEN, 0.34),
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 40px ${alpha(theme.palette.common.black, 0.32)}`,
        },
      }}
    >
      {/* Thumbnail */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '60%',
          overflow: 'hidden',
        }}
      >
        {playlist.imageUrl ? (
          <Box
            component="img"
            src={playlist.imageUrl}
            alt={playlist.name}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: gradient,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Typography sx={{ fontSize: 40, fontWeight: 900, color: alpha('#fff', 0.9) }}>
              {playlist.name[0]?.toUpperCase() ?? '♪'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Info */}
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography
          noWrap
          sx={{ fontSize: 15, fontWeight: 800, color: 'common.white', lineHeight: 1.2 }}
        >
          {playlist.name}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
          <Typography sx={{ fontSize: 12.5, color: alpha('#fff', 0.45), fontWeight: 500 }}>
            {playlist.trackCount} tracks
          </Typography>
          {!playlist.isPublic && (
            <Chip
              label="Private"
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 700,
                color: alpha('#fff', 0.5),
                bgcolor: alpha('#fff', 0.08),
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
        </Stack>

        {playlist.linkedEvents.length > 0 ? (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {playlist.linkedEvents.map((ev) => (
              <Chip
                key={ev.eventId}
                icon={<AppIcon path={iconPaths.link} sx={{ fontSize: '14px !important' }} />}
                label={ev.name}
                size="small"
                sx={{
                  maxWidth: '100%',
                  height: 24,
                  fontSize: 11,
                  fontWeight: 700,
                  color: SPOTIFY_GREEN,
                  bgcolor: alpha(SPOTIFY_GREEN, 0.12),
                  border: `1px solid ${alpha(SPOTIFY_GREEN, 0.3)}`,
                  '& .MuiChip-icon': { color: SPOTIFY_GREEN },
                  '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
            ))}
          </Stack>
        ) : (
          <Typography sx={{ mt: 1, fontSize: 11.5, color: alpha('#fff', 0.3), fontWeight: 600 }}>
            Not linked to an event
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function PlaylistSkeleton() {
  const theme = useTheme();
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
        bgcolor: alpha(theme.palette.common.white, 0.04),
        overflow: 'hidden',
      }}
    >
      <Skeleton variant="rectangular" sx={{ width: '100%', paddingTop: '60%', bgcolor: alpha('#fff', 0.06) }} />
      <Box sx={{ p: 2 }}>
        <Skeleton variant="text" width="70%" sx={{ bgcolor: alpha('#fff', 0.08) }} />
        <Skeleton variant="text" width="40%" sx={{ bgcolor: alpha('#fff', 0.06), mt: 0.5 }} />
      </Box>
    </Box>
  );
}

export function DjSpotifyPage() {
  const theme = useTheme();
  const { user, setUser } = useAuth();

  const [status, setStatus] = useState<SpotifyStatus | null>(null);
  const [playlists, setPlaylists] = useState<SpotifyPlaylist[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Handle redirect back from Spotify OAuth — show toast, update auth store, clean URL
  const notify = useNotificationStore((s) => s.show);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const error = params.get('error');
    if (connected === 'true') {
      notify('Spotify connected successfully!', 'success');
      // Patch the auth store so the dashboard stat updates immediately without
      // requiring a logout/login cycle.
      if (user) setUser({ ...user, spotifyConnected: true });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (error) {
      notify(error === 'cancelled' ? 'Spotify connection was cancelled.' : 'Spotify connection failed. Please try again.', 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [notify]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    spotifyApi
      .getStatus()
      .then((s) => {
        setStatus(s);
        if (s.connected) {
          setLoadingPlaylists(true);
          spotifyApi
            .getPlaylists()
            .then(setPlaylists)
            .finally(() => setLoadingPlaylists(false));
        }
      })
      .catch(() => setStatus({ connected: false }))
      .finally(() => setLoadingStatus(false));
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const url = await spotifyApi.getAuthUrl();
      window.location.href = url;
    } catch {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await spotifyApi.disconnect();
      if (user) setUser({ ...user, spotifyConnected: false });
      window.location.reload();
    } catch {
      setDisconnecting(false);
      setDisconnectOpen(false);
    }
  };

  const isConnected = status?.connected ?? false;

  return (
    <DjPageShell>
      <Stack spacing={{ xs: 3.5, sm: 4.5 }}>

        {/* Page header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2.25,
            border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
            bgcolor: alpha(theme.palette.common.white, 0.055),
            backdropFilter: 'blur(20px)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -80,
              right: -130,
              width: 300,
              height: 200,
              pointerEvents: 'none',
              background: `linear-gradient(115deg, transparent 0 24%, ${alpha(SPOTIFY_GREEN, 0.18)} 24% 36%, transparent 36% 52%, ${alpha(theme.palette.secondary.light, 0.14)} 52% 62%, transparent 62%)`,
              transform: 'skewY(-8deg)',
            },
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ position: 'relative', fontSize: { xs: 30, sm: 38 }, fontWeight: 900, color: 'common.white', lineHeight: 1.05 }}
            >
              Spotify
            </Typography>
            <Typography sx={{ position: 'relative', mt: 0.5, fontSize: 15, color: alpha(theme.palette.common.white, 0.58), fontWeight: 500 }}>
              {isConnected ? 'Manage your connected playlists' : 'Connect your library to power every set'}
            </Typography>
          </Box>
          {isConnected && !loadingStatus && (
            <Button
              onClick={() => setDisconnectOpen(true)}
              variant="outlined"
              size="small"
              disabled={disconnecting}
              sx={{
                position: 'relative',
                color: alpha(theme.palette.common.white, 0.58),
                borderColor: alpha(theme.palette.common.white, 0.18),
                fontSize: 13,
                borderRadius: 1.5,
                '&:hover': {
                  borderColor: theme.palette.error.main,
                  color: theme.palette.error.light,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                },
              }}
            >
              Disconnect
            </Button>
          )}
        </Stack>

        {/* Loading status skeleton */}
        {loadingStatus && (
          <Box
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
              bgcolor: alpha(theme.palette.common.white, 0.04),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Skeleton variant="circular" width={52} height={52} sx={{ bgcolor: alpha('#fff', 0.08) }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="40%" sx={{ bgcolor: alpha('#fff', 0.1) }} />
                <Skeleton variant="text" width="60%" sx={{ bgcolor: alpha('#fff', 0.07), mt: 0.5 }} />
              </Box>
            </Stack>
          </Box>
        )}

        {/* Disconnected state */}
        {!loadingStatus && !isConnected && (
          <Box
            sx={{
              p: { xs: 3, sm: 5, md: 6 },
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 2.25,
              border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
              bgcolor: alpha(theme.palette.common.white, 0.055),
              backdropFilter: 'blur(22px)',
              textAlign: 'center',
              boxShadow: `0 24px 58px ${alpha(theme.palette.common.black, 0.2)}`,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${SPOTIFY_GREEN}, ${theme.palette.secondary.light}, ${theme.palette.warning.light})`,
              },
            }}
          >
            {/* Spotify logo circle */}
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                bgcolor: alpha(SPOTIFY_GREEN, 0.12),
                border: `2px solid ${alpha(SPOTIFY_GREEN, 0.3)}`,
                display: 'grid',
                placeItems: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: `0 0 48px ${alpha(SPOTIFY_GREEN, 0.22)}`,
              }}
            >
              <AppIcon path={iconPaths.spotify} sx={{ fontSize: 46, color: SPOTIFY_GREEN }} />
            </Box>

            <Typography
              sx={{
                fontSize: { xs: 26, sm: 32, md: 36 },
                fontWeight: 900,
                color: 'common.white',
                lineHeight: 1.1,
                mb: 1.5,
              }}
            >
              Power your sets with Spotify
            </Typography>
            <Typography
              sx={{
                maxWidth: 480,
                mx: 'auto',
                fontSize: { xs: 15, sm: 17 },
                color: alpha(theme.palette.common.white, 0.58),
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              Link your playlists to events, let the crowd vote on tracks, and stay in control of the floor — all from one place.
            </Typography>

            {/* Feature bullets */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 1.5,
                mb: 4,
                textAlign: 'left',
              }}
            >
              {featureBullets.map((f) => (
                <Box
                  key={f.title}
                  sx={{
                    p: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                    bgcolor: alpha(theme.palette.common.white, 0.055),
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: alpha(SPOTIFY_GREEN, 0.55),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '34% 46% 36% 44%',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: alpha(SPOTIFY_GREEN, 0.12),
                      border: `1px solid ${alpha(SPOTIFY_GREEN, 0.2)}`,
                      mb: 1.25,
                    }}
                  >
                    <AppIcon path={f.icon} sx={{ fontSize: 18, color: SPOTIFY_GREEN }} />
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'common.white', mb: 0.5 }}>
                    {f.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: alpha(theme.palette.common.white, 0.5), lineHeight: 1.5 }}>
                    {f.copy}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Button
              onClick={handleConnect}
              disabled={connecting}
              variant="contained"
              size="large"
              startIcon={
                connecting ? (
                  <CircularProgress size={18} sx={{ color: 'inherit' }} />
                ) : (
                  <AppIcon path={iconPaths.spotify} />
                )
              }
              sx={{
                minHeight: 52,
                px: 4,
                bgcolor: SPOTIFY_GREEN,
                color: '#000',
                fontWeight: 800,
                fontSize: 16,
                '&:hover': { bgcolor: '#17a349' },
                '&.Mui-disabled': { bgcolor: alpha(SPOTIFY_GREEN, 0.4), color: alpha('#000', 0.5) },
              }}
            >
              {connecting ? 'Redirecting…' : 'Connect with Spotify'}
            </Button>
          </Box>
        )}

        {/* Connected state */}
        {!loadingStatus && isConnected && (
          <Stack spacing={3}>
            {/* Account card */}
            <Box
              sx={{
                p: { xs: 2, sm: 2.5 },
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 2,
                border: `1px solid ${alpha(SPOTIFY_GREEN, 0.25)}`,
                bgcolor: alpha(SPOTIFY_GREEN, 0.07),
                backdropFilter: 'blur(20px)',
                boxShadow: `0 20px 46px ${alpha(theme.palette.common.black, 0.16)}`,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: `linear-gradient(90deg, ${SPOTIFY_GREEN}, ${theme.palette.secondary.light})`,
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '34% 46% 36% 44%',
                    overflow: 'hidden',
                    bgcolor: alpha(SPOTIFY_GREEN, 0.15),
                    border: `2px solid ${alpha(SPOTIFY_GREEN, 0.35)}`,
                    transform: 'rotate(-4deg)',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  {status?.imageUrl ? (
                    <Box
                      component="img"
                      src={status.imageUrl}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <AppIcon path={iconPaths.person} sx={{ fontSize: 26, color: SPOTIFY_GREEN }} />
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 900, color: 'common.white' }}>
                      {status?.displayName ?? 'Spotify Account'}
                    </Typography>
                    <Chip
                      icon={<AppIcon path={iconPaths.check} sx={{ fontSize: '14px !important' }} />}
                      label="Connected"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 800,
                        color: SPOTIFY_GREEN,
                        bgcolor: alpha(SPOTIFY_GREEN, 0.14),
                        border: `1px solid ${alpha(SPOTIFY_GREEN, 0.3)}`,
                        '& .MuiChip-icon': { color: SPOTIFY_GREEN },
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                    {status?.product === 'premium' && (
                      <Chip
                        label="Premium"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 800,
                          color: alpha(theme.palette.warning.light, 0.9),
                          bgcolor: alpha(theme.palette.warning.main, 0.12),
                          border: `1px solid ${alpha(theme.palette.warning.light, 0.25)}`,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    )}
                  </Stack>
                  {status?.email && (
                    <Typography sx={{ mt: 0.35, fontSize: 13, color: alpha(theme.palette.common.white, 0.45) }}>
                      {status.email}
                      {status.followersCount !== undefined && ` · ${status.followersCount} followers`}
                    </Typography>
                  )}
                </Box>

                <AppIcon path={iconPaths.spotify} sx={{ fontSize: 28, color: SPOTIFY_GREEN, flexShrink: 0, opacity: 0.7 }} />
              </Stack>
            </Box>

            {/* Playlists section */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: alpha(theme.palette.common.white, 0.45),
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  Your playlists
                  {!loadingPlaylists && playlists.length > 0 && (
                    <Box component="span" sx={{ ml: 1, color: alpha(theme.palette.common.white, 0.25) }}>
                      {playlists.length}
                    </Box>
                  )}
                </Typography>
              </Stack>

              {loadingPlaylists ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                    gap: 1.5,
                  }}
                >
                  {Array.from({ length: 8 }).map((_, i) => (
                    <PlaylistSkeleton key={i} />
                  ))}
                </Box>
              ) : playlists.length === 0 ? (
                <Box
                  sx={{
                    p: { xs: 3, sm: 5 },
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                    bgcolor: alpha(theme.palette.common.white, 0.04),
                    textAlign: 'center',
                  }}
                >
                  <AppIcon path={iconPaths.music} sx={{ fontSize: 40, color: alpha('#fff', 0.2), mb: 1.5 }} />
                  <Typography sx={{ fontSize: 17, fontWeight: 800, color: alpha('#fff', 0.6) }}>
                    No playlists found
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 14, color: alpha('#fff', 0.35) }}>
                    Create a playlist in Spotify and it'll appear here.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, 1fr)',
                      sm: 'repeat(3, 1fr)',
                      md: 'repeat(4, 1fr)',
                      lg: 'repeat(5, 1fr)',
                    },
                    gap: 1.5,
                  }}
                >
                  {playlists.map((playlist, i) => (
                    <PlaylistCard key={playlist.playlistId} playlist={playlist} index={i} />
                  ))}
                </Box>
              )}
            </Box>
          </Stack>
        )}
      </Stack>

      <ConfirmDialog
        open={disconnectOpen}
        title="Disconnect Spotify?"
        message="Your playlists will be unlinked from all events. You can reconnect at any time."
        confirmText="Disconnect"
        cancelText="Keep connected"
        onConfirm={handleDisconnect}
        onCancel={() => setDisconnectOpen(false)}
      />
    </DjPageShell>
  );
}
